import { useMutation } from '@tanstack/react-query';
import * as Api from '@ViewModels';
import * as React from 'react';
import { toast } from 'sonner';
import { submitRegEdCompliance } from './submitRegEdCompliance';
import { invalidateRegEdSubmissions } from './useRegEdSubmissionsQuery';
import { getRegEdRevisesApprovalRequestId, IRegEdSubmissionView, RegEdComplianceContentType } from './types';

class RegEdFilingNotCompletedError extends Error {
	constructor(readonly kind: 'NoApprovalRequest' | 'NotFiled') {
		super(kind);
	}
}

interface IRevisionOwed {
	approvalRequestId: string;
	/** Which step is still owed: the repoint, or only the compliance document. */
	needsFiling: boolean;
	submission: IRegEdSubmissionView;
}

interface IArgs {
	/** Passed in rather than pulled from a hook: the email host is MobX-injected and already holds one. */
	userSession: Api.UserSessionContext;
	parentId: string | undefined;
	parentType: RegEdComplianceContentType;
	/** Rebuilt per call: the hosts derive it from a viewmodel that can change under them. */
	getImpersonationContext(): Api.IImpersonationContext | undefined;
	/** Renders the compliance document for the parent's open round. */
	regenerateCompliancePdf(impersonationContext: Api.IImpersonationContext | undefined): Promise<unknown>;
	/**
	 * The host's own follow-up once a revision is complete — reloading its viewmodel, or leaving a
	 * screen that is now showing a state the revision has left. Failures here are not the advisor's to
	 * act on, so they must not be reported as an unfiled submission.
	 */
	onRevisionFiled?(): void | Promise<void>;
	logApiError(action: string, error?: Api.IOperationResultNoValue): void;
}

/**
 * The filing half of a revision: repoint the RegEd submission onto the raised round, then render the
 * document that actually files it.
 *
 * Resumable, because either call can fail on its own — and they are not interchangeable. The repoint
 * rewrites the submission's `approvalRequestId` to the new round, so replaying it looks the prior
 * round up by an id nothing carries any more and fails ("No RegEd submission found for prior approval
 * request"). A retry has to resume at whichever step failed, which is what `needsFiling` records.
 *
 * Lives here rather than in each host because the protocol below — what the lead-time prompt owns,
 * what a landed repoint forbids, and which of the two steps a failure leaves owed — has to hold
 * identically for email, blog and social, and a copy per host is a copy that drifts.
 */
export const useRegEdRevisionFiling = ({
	userSession,
	parentId,
	parentType,
	getImpersonationContext,
	regenerateCompliancePdf,
	onRevisionFiled,
	logApiError,
}: IArgs) => {
	const [revisionOwed, setRevisionOwed] = React.useState<IRevisionOwed | null>(null);

	/** The half that runs once the repoint has landed, whenever that turns out to be. */
	const finishRevisionFiling = async (approvalRequestId: string, submission: IRegEdSubmissionView) => {
		try {
			await regenerateCompliancePdf(getImpersonationContext());
		} catch {
			// The round is open with nothing filed, which no status field reveals.
			setRevisionOwed({ approvalRequestId, needsFiling: false, submission });
			// The answers did land, so the panel's row has moved on even though the document didn't follow.
			invalidateRegEdSubmissions(parentId);
			return false;
		}
		setRevisionOwed(null);
		toast.success(
			getRegEdRevisesApprovalRequestId(submission) ? 'Sent back for compliance review.' : 'Sent for compliance review.'
		);
		// Outside the guard above: a failure here leaves the filing done, so reporting it as an unfiled
		// submission would be a lie the advisor cannot act on.
		try {
			await onRevisionFiled?.();
		} catch (error) {
			logApiError(`RegEd${parentType}Revise-Complete-Error`, Api.asApiError(error));
		}
		return true;
	};

	/**
	 * The admin left the questions to the account's user, so there is no document to render yet — their
	 * own Finish renders it. The revision itself did happen, so the host's follow-up still runs.
	 */
	const completeHandover = async (submission: IRegEdSubmissionView) => {
		setRevisionOwed(null);
		toast.success(
			getRegEdRevisesApprovalRequestId(submission)
				? "Sent back. It's waiting on the account's user to answer the compliance questions."
				: "Scheduled. It's waiting on the account's user to answer the compliance questions."
		);
		try {
			await onRevisionFiled?.();
		} catch (error) {
			logApiError(`RegEd${parentType}Revise-Complete-Error`, Api.asApiError(error));
		}
		return true;
	};

	/**
	 * Files the answers, then the document. `parkUnfiled` decides whether a filing that never landed is owed a
	 * retry: a revision has reopened a round that only that retry can fill, but a finish leaves its placeholder
	 * open, and Finish itself is the retry — parking it would hold that action behind a replay of the same answers.
	 */
	const fileAnswers = async (
		approvalRequestId: string,
		submission: IRegEdSubmissionView,
		needsFiling: boolean,
		parkUnfiled: boolean
	): Promise<{ completed: boolean; deferred: boolean }> => {
		if (!needsFiling) {
			return { completed: await finishRevisionFiling(approvalRequestId, submission), deferred: false };
		}
		// submitRegEdCompliance catches missing answers on a revision itself (and the retry parks it), but treats
		// them as nothing to file on a fresh one. Not parked: a retry would replay the same empty answers.
		if (!submission?.metadata && !getRegEdRevisesApprovalRequestId(submission)) {
			logApiError(`RegEd${parentType}Revise-MissingMetadata`);
			toast.error('The compliance answers were missing, so nothing was filed. Reopen it and send it again.');
			return { completed: false, deferred: false };
		}
		// The lead-time prompt can take ownership of the filing and succeed after this call has already
		// returned false, so the document hangs off onFiled rather than the returned boolean.
		let deferredToOverride = false;
		let handedToAccountUser = false;
		let handoverCompleted = false;
		let repointLanded = false;
		let completed = false;
		const filed = await submitRegEdCompliance({
			action: 'sent',
			approvalRequestId,
			impersonationContext: getImpersonationContext(),
			logApiError,
			metadata: submission?.metadata,
			// Completes here rather than off the return value: after a lead-time reason, the prompt's retry
			// hands over long after this call has returned.
			onDeferredToAccountUser: async () => {
				handedToAccountUser = true;
				handoverCompleted = await completeHandover(submission);
			},
			onDeferredToLeadTimeOverride: () => {
				deferredToOverride = true;
			},
			onLeadTimeOverrideDismissed: () => {
				if (parkUnfiled) {
					setRevisionOwed({ approvalRequestId, needsFiling: true, submission });
				}
			},
			onFiled: async () => {
				repointLanded = true;
				completed = await finishRevisionFiling(approvalRequestId, submission);
				return completed;
			},
			parentId,
			parentType,
			revisesApprovalRequestId: getRegEdRevisesApprovalRequestId(submission),
			userSession,
		});
		// Only when the repoint never landed. Once onFiled has run it has, and finishRevisionFiling has
		// already recorded what is still owed — overwriting here would send the retry back through a
		// repoint that cannot be replayed.
		if (parkUnfiled && !filed && !deferredToOverride && !repointLanded) {
			setRevisionOwed({ approvalRequestId, needsFiling: true, submission });
		}
		if (handedToAccountUser) {
			return { completed: handoverCompleted, deferred: false };
		}
		// Both have to hold: the filing lands before the document is rendered, and a caller that reads
		// only the filing would treat an unrendered document as a completed revision.
		return { completed: filed && completed, deferred: deferredToOverride };
	};

	const runRevisionFiling = async (approvalRequestId: string, submission: IRegEdSubmissionView, needsFiling: boolean) =>
		(await fileAnswers(approvalRequestId, submission, needsFiling, true)).completed;

	/** A refused create leaves its approval request open, so the answers file against that same round. */
	const finishSubmission = useMutation({
		mutationFn: async (submission: IRegEdSubmissionView) => {
			if (!submission.approvalRequestId) {
				throw new RegEdFilingNotCompletedError('NoApprovalRequest');
			}
			const { completed, deferred } = await fileAnswers(submission.approvalRequestId, submission, true, false);
			// The lead-time prompt owns it now and files it itself if the advisor gives a reason.
			if (!completed && !deferred) {
				throw new RegEdFilingNotCompletedError('NotFiled');
			}
			return completed;
		},
		onError: error => {
			if (error instanceof RegEdFilingNotCompletedError && error.kind === 'NotFiled') {
				// The filing has already logged and said why, as a toast or the retry card.
				return;
			}
			logApiError(`RegEd${parentType}FinishSubmission-Error`, Api.asApiError(error));
			toast.error(
				error instanceof RegEdFilingNotCompletedError
					? 'We could not tell which review to file this against. Reopen it and try again.'
					: 'We could not file the compliance answers. Please try again.'
			);
		},
	});

	const retryRevision = useMutation({
		mutationFn: (owed: IRevisionOwed) => runRevisionFiling(owed.approvalRequestId, owed.submission, owed.needsFiling),
		onError: error => {
			logApiError(`RegEd${parentType}ReviseRetry-Error`, Api.asApiError(error));
			toast.error('We could not file the compliance answers. Please try again.');
		},
	});

	// `isPending` lands a render late, and a second retry would replay a repoint that cannot be replayed.
	const retryingRef = React.useRef(false);
	const onRetryRevisionClicked = async () => {
		if (!revisionOwed || retryingRef.current) {
			return;
		}
		retryingRef.current = true;
		try {
			await retryRevision.mutateAsync(revisionOwed).catch(() => false);
		} finally {
			retryingRef.current = false;
		}
	};

	return {
		// onError has already told the advisor; resolving keeps hosts that don't await it from an unhandled rejection.
		finishSubmission: (submission: IRegEdSubmissionView) => finishSubmission.mutateAsync(submission).catch(() => false),
		isFinishingSubmission: finishSubmission.isPending,
		isRetryingRevision: retryRevision.isPending,
		onRetryRevisionClicked,
		revisionOwed,
		runRevisionFiling,
	} as const;
};
